import {
  Button,
  Chip,
  Checkbox,
  FormControl,
  FormControlLabel,
} from "@mui/material";
import type { ExamForm, ExamFormStatusType, ExamFormTest } from "@rmh/defs";
import type { ChangeEvent, Dispatch, ReactNode, SetStateAction } from "react";
import { TestType } from "../types/examForms";
import { getSubtype } from "../utils/examFormUtils";
import DisplayBinaryChoice from "./forms/DisplayBinaryChoice";
import DisplayDataInput from "./forms/DisplayDataInput";
import DisplayMultiChoice from "./forms/DisplayMultiChoice";
import DisplayRLTableChoice from "./forms/DisplayRlTableChoice";
import DisplayTable from "./forms/DisplayTable";
import ExamFormStatusTag from "./ExamFormStatusTag";

interface ExamFormDisplayProps {
  examForm: ExamForm;
  onCompleteButtonClick?: () => void;
  onInputChange?: () => void;
  onCheckboxChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  setExamState?: Dispatch<SetStateAction<ExamForm[]>>;
  hideButton?: boolean;
  hideTags?: boolean;
  completedTests?: number;
  totalTests?: number;
  disabled?: boolean;
  checkboxSelected?: boolean;
}

interface PreviewItemProps {
  item: ExamFormTest;
  examId: string;
  onInputChange?: () => void;
  setExamState?: Dispatch<SetStateAction<ExamForm[]>>;
  statusComponent: ReactNode;
  disabled: boolean;
}

const PreviewItem = ({
  item,
  setExamState,
  examId,
  onInputChange,
  statusComponent,
  disabled,
}: PreviewItemProps) => {
  const testType = getSubtype(item);

  if (
    item.__typename === "ExamFormTestChoice" &&
    (testType === (TestType.BinaryChoice as string) ||
      testType === (TestType.YesNoChoice as string) ||
      testType === (TestType.PosNegChoice as string))
  ) {
    return (
      <DisplayBinaryChoice
        disabled={disabled}
        examId={examId}
        item={item}
        onInputChange={onInputChange}
        setExamState={setExamState}
        statusComponent={statusComponent}
      />
    );
  }
  if (
    item.__typename === "ExamFormTestChoice" &&
    (testType === (TestType.MultipleChoice as string) ||
      testType === (TestType.MultipleSelect as string) ||
      testType === (TestType.MultipleSelectDropdown as string))
  ) {
    return (
      <DisplayMultiChoice
        disabled={disabled}
        examId={examId}
        item={item}
        onInputChange={onInputChange}
        setExamState={setExamState}
        statusComponent={statusComponent}
      />
    );
  }
  if (
    item.__typename === "ExamFormTestData" &&
    testType === (TestType.ExamFormDataInput as string)
  ) {
    return (
      <DisplayDataInput
        disabled={disabled}
        examId={examId}
        item={item}
        onInputChange={onInputChange}
        setExamState={setExamState}
        statusComponent={statusComponent}
      />
    );
  }
  if (
    item.__typename === "ExamFormTestTable" &&
    testType === (TestType.ExamFormRLTable as string)
  ) {
    return (
      <DisplayTable
        disabled={disabled}
        examId={examId}
        item={item}
        onInputChange={onInputChange}
        setExamState={setExamState}
        statusComponent={statusComponent}
      />
    );
  }
  if (
    item.__typename === "ExamFormTestTable" &&
    testType === (TestType.ExamFormRLTableChoice as string)
  ) {
    return (
      <DisplayRLTableChoice
        disabled={disabled}
        examId={examId}
        item={item}
        onInputChange={onInputChange}
        setExamState={setExamState}
        statusComponent={statusComponent}
      />
    );
  }
  return null;
};

function ExamFormDisplay({
  examForm,
  onCompleteButtonClick,
  hideButton = false,
  hideTags = false,
  completedTests = 0,
  totalTests,
  setExamState,
  onInputChange,
  onCheckboxChange,
  checkboxSelected,
  disabled = false,
}: ExamFormDisplayProps){
  const status =
    examForm.examFormStatus && examForm.examFormStatus.length > 0
      ? (examForm.examFormStatus[examForm.examFormStatus.length - 1]
          ?.examFormStatusType as ExamFormStatusType)
      : undefined;
  const statusComponent = <ExamFormStatusTag status={status} />;

  return (
    <>
      {Boolean(!hideTags) ||
        (Boolean(!hideButton) && (
          <div className="mt-9 flex items-center justify-center">
            {Boolean(!hideTags) && (
              <div className="flex w-3/4 flex-wrap gap-2 ">
                {[
                  ...(Array.isArray(examForm.bodySegmentTags)
                    ? examForm.bodySegmentTags
                    : []),
                  ...(Array.isArray(examForm.bodyRegionTags)
                    ? examForm.bodyRegionTags
                    : []),
                  ...(Array.isArray(examForm.levelTags)
                    ? examForm.levelTags
                    : []),
                  ...(Array.isArray(examForm.bodyPositionTags)
                    ? examForm.bodyPositionTags
                    : []),
                  ...(Array.isArray(examForm.triageSetTags)
                    ? examForm.triageSetTags
                    : []),
                  ...(Array.isArray(examForm.diagnosisTags)
                    ? examForm.diagnosisTags
                    : []),
                  ...(Array.isArray(examForm.functionTags)
                    ? examForm.functionTags
                    : []),
                  ...(Array.isArray(examForm.familyTags)
                    ? examForm.familyTags
                    : []),
                ].map((tag) =>
                  tag ? <Chip key={tag} label={tag} size="small" /> : null
                )}
              </div>
            )}
            {Boolean(!hideButton) && (
              <FormControl className="flex flex-col pl-3 self-start">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checkboxSelected}
                      className="mr-2"
                      onChange={onCheckboxChange}
                    />
                  }
                  label="Pin Form for Reporting"
                />
              </FormControl>
            )}
            {Boolean(!hideButton) && (
              <div className="ml-auto flex flex-col items-center justify-center">
                <p>
                  {" "}
                  {completedTests} /{" "}
                  {totalTests || examForm.examFormTests.length} Tests Completed
                </p>
                <Button
                  className="ml-auto mt-2"
                  color="primary"
                  data-cy="objexam-mark-form-complete-button"
                  onClick={onCompleteButtonClick}
                  variant="contained"
                >
                  Mark Form as Complete
                </Button>
              </div>
            )}
          </div>
        ))}
      <div className="mt-7 flex flex-col gap-7">
        {examForm.examFormTests.map((item: ExamFormTest | null) => {
          return item ? (
            <PreviewItem
              disabled={disabled}
              examId={examForm.examFormId}
              item={item}
              key={`exam_render_${item.testTitle}`}
              onInputChange={onInputChange}
              setExamState={setExamState}
              statusComponent={statusComponent}
            />
          ) : null;
        })}
      </div>
    </>
  );
}

export default ExamFormDisplay;
